/**
 * The form for one node. Fields follow `src/shared/scenario/schema.ts` exactly, type by type, and
 * every id another part of the scenario points at is edited through `IdField`, which renames the
 * references with it.
 */
import type {
  BranchNode,
  CallNode,
  DecisionNode,
  NarrativeNode,
  PollNode,
  RevealNode,
  Scenario,
  ScenarioNode,
} from "@shared/scenario";
import { useState } from "preact/hooks";
import { Button, Dialog, QualityPill, Segmented, Toggle } from "../../components/ui";
import { useT } from "../../i18n";
import {
  FormGroup,
  IdField,
  KeyNumberMap,
  NumberField,
  RoleChecks,
  RowTools,
  SelectField,
  StringList,
  TextArea,
  TextField,
} from "./fields";
import { bestPossible, countRefs, freshId, KEBAB, move, newOption, renameId } from "./model";

/**
 * Change the working copy: mutate the clone you are handed, or return a whole new scenario (a
 * rename does). Typed `unknown` so both shapes of callback fit without a void-in-union.
 */
export type Edit = (fn: (s: Scenario) => unknown) => void;

const ARTIFACT_KINDS = ["email", "sms", "chat", "ticket", "news", "memo", "alert"] as const;
const OPS = [">", ">=", "<", "<=", "==", "!="] as const;
const QUALITIES = ["best", "good", "risky", "bad"] as const;

/** Options for a `next`-style select: every node, labelled by id and title. */
function useNodeOptions(s: Scenario, nullable: boolean) {
  const t = useT();
  const list = Object.entries(s.nodes).map(([id, n]) => ({ value: id, label: `${id} · ${n.title}` }));
  return nullable ? [{ value: "", label: t("build.field.none") }, ...list] : list;
}

export function NodeForm({
  scenario,
  id,
  edit,
  onSelect,
}: {
  scenario: Scenario;
  id: string;
  edit: Edit;
  /** Called with the node's new id after a rename or duplicate, or null after a delete. */
  onSelect: (id: string | null) => void;
}) {
  const t = useT();
  const node = scenario.nodes[id];
  const [confirmDelete, setConfirmDelete] = useState(false);
  if (!node) return null;
  const base = `nodes.${id}`;
  const editNode = <T extends ScenarioNode>(fn: (n: T) => void) =>
    edit((s) => {
      const n = s.nodes[id];
      if (n) fn(n as T);
    });
  const refs = countRefs(scenario, "node", id);
  const isStart = scenario.start === id;

  return (
    <div class="stack build-form-body">
      <div class="build-form-head">
        <div class="build-form-title">
          <span class="node-type" title={t(`build.type.${node.type}`)}>
            {node.type}
          </span>
          <h2>{node.title || id}</h2>
          {isStart ? <span class="pill accent">{t("build.node.isStart")}</span> : null}
        </div>
        <div class="row">
          {isStart ? null : (
            <Button
              variant="secondary"
              size="sm"
              icon="flag-triangle-right"
              onClick={() =>
                edit((s) => {
                  s.start = id;
                })
              }
            >
              {t("build.node.setStart")}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon="copy"
            onClick={() => {
              const copyId = freshId(`${id}-copy`, Object.keys(scenario.nodes));
              edit((s) => {
                const src = s.nodes[id];
                if (src) s.nodes[copyId] = structuredClone(src);
              });
              onSelect(copyId);
            }}
          >
            {t("build.node.duplicate")}
          </Button>
          <Button variant="ghost" size="sm" icon="trash" onClick={() => setConfirmDelete(true)}>
            {t("action.delete")}
          </Button>
        </div>
      </div>

      <FormGroup title={t("build.group.basics")}>
        <IdField
          label={t("build.field.nodeId")}
          path={`${base}.id`}
          value={id}
          taken={Object.keys(scenario.nodes)}
          pattern={KEBAB}
          refs={refs}
          help={t("build.help.nodeId")}
          onRename={(to) => {
            edit((s) => renameId(s, "node", id, to));
            onSelect(to);
          }}
        />
        <TextField
          label={t("build.field.title")}
          path={`${base}.title`}
          value={node.title}
          help={t("build.help.nodeTitle")}
          onChange={(v) =>
            editNode((n) => {
              n.title = v;
            })
          }
        />
      </FormGroup>

      {node.type === "narrative" ? (
        <NarrativeFields s={scenario} id={id} node={node} edit={editNode} />
      ) : null}
      {node.type === "call" ? <CallFields s={scenario} id={id} node={node} edit={editNode} /> : null}
      {node.type === "decision" ? <DecisionFields s={scenario} id={id} node={node} edit={editNode} /> : null}
      {node.type === "poll" ? <PollFields s={scenario} id={id} node={node} edit={editNode} /> : null}
      {node.type === "reveal" ? <RevealFields s={scenario} id={id} node={node} edit={editNode} /> : null}
      {node.type === "branch" ? <BranchFields s={scenario} id={id} node={node} edit={editNode} /> : null}
      {node.type === "debrief" ? (
        <FormGroup title={t("build.group.content")}>
          <TextArea
            label={t("build.field.body")}
            path={`${base}.body`}
            value={node.body}
            rows={4}
            help={t("build.help.debriefBody")}
            onChange={(v) =>
              editNode<Extract<ScenarioNode, { type: "debrief" }>>((n) => {
                n.body = v;
              })
            }
          />
        </FormGroup>
      ) : null}

      <FormGroup title={t("build.group.notes")}>
        <TextArea
          label={t("build.field.facilitatorNotes")}
          path={`${base}.facilitatorNotes`}
          value={node.facilitatorNotes ?? ""}
          help={t("build.help.facilitatorNotes")}
          onChange={(v) =>
            editNode((n) => {
              if (v) n.facilitatorNotes = v;
              else delete n.facilitatorNotes;
            })
          }
        />
        <div class="field" data-path={`${base}.roleNotes`}>
          <div class="label">{t("build.field.roleNotes")}</div>
          <div class="help">{t("build.help.roleNotes")}</div>
        </div>
        {scenario.roles.map((r) => (
          <TextArea
            key={r.id}
            label={r.name}
            path={`${base}.roleNotes.${r.id}`}
            value={node.roleNotes[r.id] ?? ""}
            rows={2}
            onChange={(v) =>
              editNode((n) => {
                if (v) n.roleNotes[r.id] = v;
                else delete n.roleNotes[r.id];
              })
            }
          />
        ))}
        {Object.keys(node.roleNotes)
          .filter((k) => !scenario.roles.some((r) => r.id === k))
          .map((k) => (
            <div key={k} class="row">
              <span class="error-text">{t("build.roleNotes.unknown", { id: k })}</span>
              <Button
                variant="secondary"
                size="sm"
                icon="trash"
                onClick={() =>
                  editNode((n) => {
                    delete n.roleNotes[k];
                  })
                }
              >
                {t("build.kv.clear")}
              </Button>
            </div>
          ))}
      </FormGroup>

      {confirmDelete ? (
        <Dialog title={t("build.node.deleteTitle")} onClose={() => setConfirmDelete(false)}>
          {isStart ? (
            <div class="stack">
              <p>{t("build.node.deleteStart")}</p>
              <div class="row">
                <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                  {t("action.close")}
                </Button>
              </div>
            </div>
          ) : (
            <div class="stack">
              <p>{t("build.node.deleteConfirm", { title: node.title || id, count: refs })}</p>
              <div class="row">
                <Button
                  variant="danger"
                  onClick={() => {
                    setConfirmDelete(false);
                    edit((s) => {
                      delete s.nodes[id];
                    });
                    onSelect(null);
                  }}
                >
                  {t("action.delete")}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                  {t("action.cancel")}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}

type EditNode = <T extends ScenarioNode>(fn: (n: T) => void) => void;

function NextField({
  s,
  path,
  value,
  onChange,
  nullable,
  label,
  help,
}: {
  s: Scenario;
  path: string;
  value: string | null;
  onChange: (v: string | null) => void;
  nullable: boolean;
  label?: string;
  help?: string | null;
}) {
  const t = useT();
  const options = useNodeOptions(s, nullable);
  return (
    <SelectField
      label={label ?? t("build.field.next")}
      path={path}
      value={value ?? ""}
      options={options}
      help={help === undefined ? t("build.help.next") : help}
      onChange={(v) => onChange(v === "" && nullable ? null : v)}
    />
  );
}

function NarrativeFields({
  s,
  id,
  node,
  edit,
}: {
  s: Scenario;
  id: string;
  node: NarrativeNode;
  edit: EditNode;
}) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: NarrativeNode) => void) => edit<NarrativeNode>(fn);
  const a = node.artifact;
  return (
    <>
      <FormGroup title={t("build.group.content")}>
        <TextArea
          label={t("build.field.body")}
          path={`${base}.body`}
          value={node.body}
          rows={4}
          help={t("build.help.narrativeBody")}
          onChange={(v) =>
            e((n) => {
              n.body = v;
            })
          }
        />
        <NextField
          s={s}
          path={`${base}.next`}
          value={node.next}
          nullable
          onChange={(v) =>
            e((n) => {
              n.next = v;
            })
          }
        />
      </FormGroup>
      <FormGroup title={t("build.group.artifact")} path={`${base}.artifact`}>
        <Toggle
          label={t("build.field.artifactOn")}
          help={t("build.help.artifactOn")}
          checked={Boolean(a)}
          onChange={(on) =>
            e((n) => {
              if (on) n.artifact = { kind: "email", body: n.body };
              else delete n.artifact;
            })
          }
        />
        {a ? (
          <>
            <SelectField
              label={t("build.field.artifactKind")}
              path={`${base}.artifact.kind`}
              value={a.kind}
              options={ARTIFACT_KINDS.map((k) => ({ value: k, label: t(`build.artifact.${k}`) }))}
              help={t("build.help.artifactKind")}
              onChange={(v) =>
                e((n) => {
                  if (n.artifact) n.artifact.kind = v;
                })
              }
            />
            <div class="build-pair">
              {(["from", "to"] as const).map((k) => (
                <TextField
                  key={k}
                  label={t(`build.field.artifact.${k}`)}
                  path={`${base}.artifact.${k}`}
                  value={a[k] ?? ""}
                  onChange={(v) =>
                    e((n) => {
                      if (!n.artifact) return;
                      if (v) n.artifact[k] = v;
                      else delete n.artifact[k];
                    })
                  }
                />
              ))}
            </div>
            <div class="build-pair">
              {(["subject", "timestamp"] as const).map((k) => (
                <TextField
                  key={k}
                  label={t(`build.field.artifact.${k}`)}
                  path={`${base}.artifact.${k}`}
                  value={a[k] ?? ""}
                  onChange={(v) =>
                    e((n) => {
                      if (!n.artifact) return;
                      if (v) n.artifact[k] = v;
                      else delete n.artifact[k];
                    })
                  }
                />
              ))}
            </div>
            <TextArea
              label={t("build.field.artifactBody")}
              path={`${base}.artifact.body`}
              value={a.body}
              rows={4}
              help={t("build.help.artifactBody")}
              onChange={(v) =>
                e((n) => {
                  if (n.artifact) n.artifact.body = v;
                })
              }
            />
          </>
        ) : null}
      </FormGroup>
    </>
  );
}

function CallFields({ s, id, node, edit }: { s: Scenario; id: string; node: CallNode; edit: EditNode }) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: CallNode) => void) => edit<CallNode>(fn);
  return (
    <>
      <FormGroup title={t("build.group.caller")}>
        <div class="build-pair">
          <TextField
            label={t("build.field.callerName")}
            path={`${base}.caller.name`}
            value={node.caller.name}
            onChange={(v) =>
              e((n) => {
                n.caller.name = v;
              })
            }
          />
          <TextField
            label={t("build.field.displayNumber")}
            path={`${base}.caller.displayNumber`}
            value={node.caller.displayNumber}
            help={t("build.help.displayNumber")}
            onChange={(v) =>
              e((n) => {
                n.caller.displayNumber = v;
              })
            }
          />
        </div>
        <Toggle
          label={t("build.field.spoofed")}
          help={t("build.help.spoofed")}
          checked={node.caller.spoofed}
          onChange={(v) =>
            e((n) => {
              n.caller.spoofed = v;
            })
          }
        />
        <TextField
          label={t("build.field.voice")}
          path={`${base}.caller.voice`}
          value={node.caller.voice ?? ""}
          help={t("build.help.voice")}
          onChange={(v) =>
            e((n) => {
              if (v) n.caller.voice = v;
              else delete n.caller.voice;
            })
          }
        />
      </FormGroup>
      <FormGroup title={t("build.group.callee")} path={`${base}.callee`}>
        <div class="build-pair">
          <TextField
            label={t("build.field.calleeName")}
            path={`${base}.callee.name`}
            value={node.callee.name}
            onChange={(v) =>
              e((n) => {
                n.callee.name = v;
              })
            }
          />
          <SelectField
            label={t("build.field.calleeRole")}
            path={`${base}.callee.roleId`}
            value={node.callee.roleId ?? ""}
            options={[
              { value: "", label: t("build.field.none") },
              ...s.roles.map((r) => ({ value: r.id, label: r.name })),
            ]}
            help={t("build.help.calleeRole")}
            onChange={(v) =>
              e((n) => {
                if (v) n.callee.roleId = v;
                else delete n.callee.roleId;
              })
            }
          />
        </div>
      </FormGroup>
      <FormGroup
        title={t("build.group.transcript")}
        path={`${base}.transcript`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            onClick={() =>
              e((n) => {
                const last = n.transcript[n.transcript.length - 1];
                n.transcript.push({ speaker: last?.speaker === "caller" ? "callee" : "caller", text: "…" });
              })
            }
          >
            {t("build.transcript.add")}
          </Button>
        }
      >
        <p class="muted small">{t("build.help.transcript")}</p>
        <ol class="build-cards">
          {node.transcript.map((line, i) => {
            const p = `${base}.transcript.${i}`;
            const rowLabel = t("build.transcript.line", { n: i + 1 });
            return (
              <li key={i} class="build-card" data-path={p}>
                <div class="build-card-head">
                  <span class="eyebrow">{rowLabel}</span>
                  <RowTools
                    label={rowLabel}
                    index={i}
                    count={node.transcript.length}
                    min={1}
                    onMove={(d) =>
                      e((n) => {
                        n.transcript = move(n.transcript, i, d);
                      })
                    }
                    onRemove={() =>
                      e((n) => {
                        n.transcript.splice(i, 1);
                      })
                    }
                  />
                </div>
                <div class="build-pair">
                  <SelectField
                    label={t("build.field.speaker")}
                    path={`${p}.speaker`}
                    value={line.speaker}
                    options={(["caller", "callee", "system"] as const).map((k) => ({
                      value: k,
                      label: t(`build.speaker.${k}`),
                    }))}
                    onChange={(v) =>
                      e((n) => {
                        const l = n.transcript[i];
                        if (l) l.speaker = v;
                      })
                    }
                  />
                  <TextField
                    label={t("build.field.speakerName")}
                    path={`${p}.name`}
                    value={line.name ?? ""}
                    help={t("build.help.speakerName")}
                    onChange={(v) =>
                      e((n) => {
                        const l = n.transcript[i];
                        if (!l) return;
                        if (v) l.name = v;
                        else delete l.name;
                      })
                    }
                  />
                </div>
                <TextArea
                  label={t("build.field.lineText")}
                  path={`${p}.text`}
                  value={line.text}
                  rows={2}
                  onChange={(v) =>
                    e((n) => {
                      const l = n.transcript[i];
                      if (l) l.text = v;
                    })
                  }
                />
                <div class="build-pair">
                  <TextField
                    label={t("build.field.tell")}
                    path={`${p}.tell`}
                    value={line.tell ?? ""}
                    help={t("build.help.tell")}
                    onChange={(v) =>
                      e((n) => {
                        const l = n.transcript[i];
                        if (!l) return;
                        if (v) l.tell = v;
                        else delete l.tell;
                      })
                    }
                  />
                  <NumberField
                    label={t("build.field.pauseMs")}
                    path={`${p}.pauseMs`}
                    value={line.pauseMs}
                    min={0}
                    max={20000}
                    step={250}
                    optional
                    help={t("build.help.pauseMs")}
                    onChange={(v) =>
                      e((n) => {
                        const l = n.transcript[i];
                        if (!l) return;
                        if (v === undefined) delete l.pauseMs;
                        else l.pauseMs = v;
                      })
                    }
                  />
                </div>
              </li>
            );
          })}
        </ol>
      </FormGroup>
      <FormGroup title={t("build.group.afterCall")}>
        <StringList
          label={t("build.field.redFlags")}
          path={`${base}.redFlags`}
          items={node.redFlags}
          itemLabel={t("build.item.redFlag")}
          help={t("build.help.redFlags")}
          onChange={(v) =>
            e((n) => {
              n.redFlags = v;
            })
          }
        />
        <NextField
          s={s}
          path={`${base}.next`}
          value={node.next}
          nullable
          onChange={(v) =>
            e((n) => {
              n.next = v;
            })
          }
        />
      </FormGroup>
    </>
  );
}

function DecisionFields({
  s,
  id,
  node,
  edit,
}: {
  s: Scenario;
  id: string;
  node: DecisionNode;
  edit: EditNode;
}) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: DecisionNode) => void) => edit<DecisionNode>(fn);
  const possible = bestPossible(node);
  const dims = s.dimensions.map((d) => ({ id: d.id, label: d.label }));
  const vars = Object.keys(s.variables).map((v) => ({ id: v, label: v }));
  const nodeOptions = useNodeOptions(s, false);
  const summary = s.dimensions
    .filter((d) => (possible[d.id] ?? 0) > 0)
    .map((d) => `${d.label} ${possible[d.id]}`)
    .join(" · ");
  return (
    <>
      <FormGroup title={t("build.group.vote")}>
        <TextArea
          label={t("build.field.prompt")}
          path={`${base}.prompt`}
          value={node.prompt}
          rows={2}
          help={t("build.help.prompt")}
          onChange={(v) =>
            e((n) => {
              n.prompt = v;
            })
          }
        />
        <TextArea
          label={t("build.field.context")}
          path={`${base}.context`}
          value={node.context ?? ""}
          rows={2}
          help={t("build.help.context")}
          onChange={(v) =>
            e((n) => {
              if (v) n.context = v;
              else delete n.context;
            })
          }
        />
        <NumberField
          label={t("build.field.timerSec")}
          path={`${base}.timerSec`}
          value={node.timerSec}
          min={10}
          max={900}
          step={5}
          help={t("build.help.timerSec")}
          onChange={(v) =>
            e((n) => {
              if (v !== undefined) n.timerSec = v;
            })
          }
        />
        <Toggle
          label={t("build.field.votersAll")}
          help={t("build.help.voters")}
          checked={node.voters === "all"}
          onChange={(all) =>
            e((n) => {
              n.voters = all ? "all" : s.roles.slice(0, 1).map((r) => r.id);
            })
          }
        />
        {node.voters !== "all" ? (
          <RoleChecks
            label={t("build.field.voters")}
            path={`${base}.voters`}
            roles={s.roles}
            value={node.voters}
            onChange={(v) =>
              e((n) => {
                n.voters = v.length ? v : "all";
              })
            }
          />
        ) : null}
      </FormGroup>

      <FormGroup
        title={t("build.group.options")}
        path={`${base}.options`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            disabled={node.options.length >= 6}
            onClick={() =>
              e((n) => {
                const oid = freshId(
                  "option",
                  n.options.map((o) => o.id),
                );
                n.options.push(newOption(oid, "good", n.options[0]?.next ?? s.start));
              })
            }
          >
            {t("build.option.add")}
          </Button>
        }
      >
        <p class="muted small">{t("build.help.options")}</p>
        <p class="build-possible small" data-path={`${base}.possible`}>
          <strong>{t("build.option.bestPossible")}</strong>{" "}
          <span class="mono">{summary || t("build.option.bestPossibleNone")}</span>
        </p>
        <ol class="build-cards">
          {node.options.map((o, i) => {
            const p = `${base}.options.${i}`;
            const rowLabel = t("build.option.n", { n: i + 1 });
            return (
              <li key={`${i}-${o.id}`} class="build-card" data-path={`${base}.options.${o.id}`}>
                <div class="build-card-head">
                  <span class="build-card-title">
                    <span class="eyebrow">{rowLabel}</span>
                    <QualityPill quality={o.quality} />
                  </span>
                  <RowTools
                    label={rowLabel}
                    index={i}
                    count={node.options.length}
                    min={2}
                    onMove={(d) =>
                      e((n) => {
                        n.options = move(n.options, i, d);
                      })
                    }
                    onRemove={() =>
                      e((n) => {
                        n.options.splice(i, 1);
                      })
                    }
                  />
                </div>
                <div class="build-pair">
                  <IdField
                    label={t("build.field.optionId")}
                    path={`${p}.id`}
                    value={o.id}
                    taken={node.options.map((x) => x.id)}
                    pattern={KEBAB}
                    onRename={(to) =>
                      e((n) => {
                        const opt = n.options[i];
                        if (opt) opt.id = to;
                      })
                    }
                  />
                  <SelectField
                    label={t("build.field.next")}
                    path={`${p}.next`}
                    value={o.next}
                    options={nodeOptions}
                    help={t("build.help.optionNext")}
                    onChange={(v) =>
                      e((n) => {
                        const opt = n.options[i];
                        if (opt) opt.next = v;
                      })
                    }
                  />
                </div>
                <TextField
                  label={t("build.field.optionLabel")}
                  path={`${p}.label`}
                  value={o.label}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.label = v;
                    })
                  }
                />
                <TextField
                  label={t("build.field.optionDetail")}
                  path={`${p}.detail`}
                  value={o.detail ?? ""}
                  help={t("build.help.optionDetail")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (!opt) return;
                      if (v) opt.detail = v;
                      else delete opt.detail;
                    })
                  }
                />
                <div data-path={`${p}.quality`}>
                  <Segmented
                    label={t("build.field.quality")}
                    value={o.quality}
                    options={QUALITIES.map((q) => ({ value: q, label: t(`quality.${q}`) }))}
                    onChange={(v) =>
                      e((n) => {
                        const opt = n.options[i];
                        if (opt) opt.quality = v;
                      })
                    }
                  />
                </div>
                <TextArea
                  label={t("build.field.feedback")}
                  path={`${p}.feedback`}
                  value={o.feedback}
                  rows={2}
                  help={t("build.help.feedback")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.feedback = v;
                    })
                  }
                />
                <KeyNumberMap
                  label={t("build.field.score")}
                  path={`${p}.effects.score`}
                  keys={dims}
                  values={o.effects.score}
                  help={t("build.help.score")}
                  emptyText={t("build.kv.noDimensions")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.effects.score = v;
                    })
                  }
                />
                <KeyNumberMap
                  label={t("build.field.vars")}
                  path={`${p}.effects.vars`}
                  keys={vars}
                  values={o.effects.vars}
                  help={t("build.help.vars")}
                  emptyText={t("build.kv.noVariables")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.effects.vars = v;
                    })
                  }
                />
                <RoleChecks
                  label={t("build.field.roleBonus")}
                  path={`${p}.roleBonus`}
                  roles={s.roles}
                  value={o.roleBonus}
                  help={t("build.help.roleBonus")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.roleBonus = v;
                    })
                  }
                />
                <StringList
                  label={t("build.field.tags")}
                  path={`${p}.tags`}
                  items={o.tags}
                  itemLabel={t("build.item.tag")}
                  help={t("build.help.optionTags")}
                  onChange={(v) =>
                    e((n) => {
                      const opt = n.options[i];
                      if (opt) opt.tags = v;
                    })
                  }
                />
              </li>
            );
          })}
        </ol>
      </FormGroup>
    </>
  );
}

function PollFields({ s, id, node, edit }: { s: Scenario; id: string; node: PollNode; edit: EditNode }) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: PollNode) => void) => edit<PollNode>(fn);
  return (
    <>
      <FormGroup title={t("build.group.poll")}>
        <TextArea
          label={t("build.field.prompt")}
          path={`${base}.prompt`}
          value={node.prompt}
          rows={2}
          help={t("build.help.pollPrompt")}
          onChange={(v) =>
            e((n) => {
              n.prompt = v;
            })
          }
        />
        <NumberField
          label={t("build.field.timerSec")}
          path={`${base}.timerSec`}
          value={node.timerSec}
          min={10}
          max={900}
          step={5}
          help={t("build.help.timerSec")}
          onChange={(v) =>
            e((n) => {
              if (v !== undefined) n.timerSec = v;
            })
          }
        />
        <NextField
          s={s}
          path={`${base}.next`}
          value={node.next}
          nullable
          onChange={(v) =>
            e((n) => {
              n.next = v;
            })
          }
        />
      </FormGroup>
      <FormGroup
        title={t("build.group.options")}
        path={`${base}.options`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            disabled={node.options.length >= 8}
            onClick={() =>
              e((n) => {
                n.options.push({
                  id: freshId(
                    "answer",
                    n.options.map((o) => o.id),
                  ),
                  label: "…",
                });
              })
            }
          >
            {t("build.option.add")}
          </Button>
        }
      >
        <ol class="build-cards">
          {node.options.map((o, i) => {
            const p = `${base}.options.${i}`;
            const rowLabel = t("build.option.n", { n: i + 1 });
            return (
              <li key={`${i}-${o.id}`} class="build-card" data-path={p}>
                <div class="build-card-head">
                  <span class="eyebrow">{rowLabel}</span>
                  <RowTools
                    label={rowLabel}
                    index={i}
                    count={node.options.length}
                    min={2}
                    onMove={(d) =>
                      e((n) => {
                        n.options = move(n.options, i, d);
                      })
                    }
                    onRemove={() =>
                      e((n) => {
                        n.options.splice(i, 1);
                      })
                    }
                  />
                </div>
                <div class="build-pair">
                  <IdField
                    label={t("build.field.optionId")}
                    path={`${p}.id`}
                    value={o.id}
                    taken={node.options.map((x) => x.id)}
                    pattern={KEBAB}
                    onRename={(to) =>
                      e((n) => {
                        const opt = n.options[i];
                        if (opt) opt.id = to;
                      })
                    }
                  />
                  <TextField
                    label={t("build.field.optionLabel")}
                    path={`${p}.label`}
                    value={o.label}
                    onChange={(v) =>
                      e((n) => {
                        const opt = n.options[i];
                        if (opt) opt.label = v;
                      })
                    }
                  />
                </div>
              </li>
            );
          })}
        </ol>
      </FormGroup>
    </>
  );
}

function RevealFields({ s, id, node, edit }: { s: Scenario; id: string; node: RevealNode; edit: EditNode }) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: RevealNode) => void) => edit<RevealNode>(fn);
  return (
    <FormGroup title={t("build.group.content")}>
      <TextArea
        label={t("build.field.body")}
        path={`${base}.body`}
        value={node.body}
        rows={4}
        help={t("build.help.revealBody")}
        onChange={(v) =>
          e((n) => {
            n.body = v;
          })
        }
      />
      <StringList
        label={t("build.field.lessons")}
        path={`${base}.lessons`}
        items={node.lessons}
        itemLabel={t("build.item.lesson")}
        multiline
        help={t("build.help.lessons")}
        onChange={(v) =>
          e((n) => {
            n.lessons = v;
          })
        }
      />
      <NextField
        s={s}
        path={`${base}.next`}
        value={node.next}
        nullable
        onChange={(v) =>
          e((n) => {
            n.next = v;
          })
        }
      />
    </FormGroup>
  );
}

function BranchFields({ s, id, node, edit }: { s: Scenario; id: string; node: BranchNode; edit: EditNode }) {
  const t = useT();
  const base = `nodes.${id}`;
  const e = (fn: (n: BranchNode) => void) => edit<BranchNode>(fn);
  const vars = Object.keys(s.variables).map((v) => ({ value: v, label: v }));
  return (
    <FormGroup
      title={t("build.group.conditions")}
      path={`${base}.conditions`}
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() =>
            e((n) => {
              n.conditions.push({ var: vars[0]?.value ?? "", op: ">=", value: 1, next: n.else });
            })
          }
        >
          {t("build.condition.add")}
        </Button>
      }
    >
      <p class="muted small">{t("build.help.conditions")}</p>
      <ol class="build-cards">
        {node.conditions.map((c, i) => {
          const p = `${base}.conditions.${i}`;
          const rowLabel = t("build.condition.n", { n: i + 1 });
          return (
            <li key={i} class="build-card" data-path={p}>
              <div class="build-card-head">
                <span class="eyebrow">{rowLabel}</span>
                <RowTools
                  label={rowLabel}
                  index={i}
                  count={node.conditions.length}
                  min={1}
                  onMove={(d) =>
                    e((n) => {
                      n.conditions = move(n.conditions, i, d);
                    })
                  }
                  onRemove={() =>
                    e((n) => {
                      n.conditions.splice(i, 1);
                    })
                  }
                />
              </div>
              <div class="build-triple">
                <SelectField
                  label={t("build.field.var")}
                  path={`${p}.var`}
                  value={c.var}
                  options={vars}
                  onChange={(v) =>
                    e((n) => {
                      const x = n.conditions[i];
                      if (x) x.var = v;
                    })
                  }
                />
                <SelectField
                  label={t("build.field.op")}
                  path={`${p}.op`}
                  value={c.op}
                  options={OPS.map((o) => ({ value: o, label: o }))}
                  onChange={(v) =>
                    e((n) => {
                      const x = n.conditions[i];
                      if (x) x.op = v;
                    })
                  }
                />
                <NumberField
                  label={t("build.field.value")}
                  path={`${p}.value`}
                  value={c.value}
                  onChange={(v) =>
                    e((n) => {
                      const x = n.conditions[i];
                      if (x && v !== undefined) x.value = v;
                    })
                  }
                />
              </div>
              <NextField
                s={s}
                path={`${p}.next`}
                value={c.next}
                nullable={false}
                help={null}
                onChange={(v) =>
                  e((n) => {
                    const x = n.conditions[i];
                    if (x && v) x.next = v;
                  })
                }
              />
            </li>
          );
        })}
      </ol>
      {vars.length ? null : <p class="error-text small">{t("build.condition.noVariables")}</p>}
      <NextField
        s={s}
        path={`${base}.else`}
        label={t("build.field.else")}
        value={node.else}
        nullable={false}
        help={t("build.help.else")}
        onChange={(v) =>
          e((n) => {
            if (v) n.else = v;
          })
        }
      />
    </FormGroup>
  );
}
